<template>
    <div class="stand-pagination-table"
        role="table"
        :aria-rowcount="ariaRowCount"
        :aria-colcount="ariaColCount">
        <div v-if="showToolbar" class="table-toolbar">
            <div class="toolbar-left">
                <Button v-if="enableRefresh"
                    type="default"
                    size="small"
                    :disabled="refreshRemainingMs > 0"
                    @click="handleRefreshClick">
                    刷新<span v-if="refreshRemainingMs > 0">（{{ Math.ceil(refreshRemainingMs / 1000) }}s）</span>
                </Button>
            </div>
            <div class="toolbar-right">
                <div v-if="enableVxeVirtual" class="virtual-toggle">
                    <span class="virtual-toggle-label">虚拟滚动</span>
                    <i-switch
                        size="small"
                        :value="virtualSwitchValue"
                        :disabled="!virtualSwitchAvailable"
                        @on-change="handleVirtualToggle" />
                </div>

                <Dropdown v-if="enableExport" placement="bottom-end" transfer>
                    <Button type="default" size="small">
                        导出
                        <Icon type="ios-arrow-down" />
                    </Button>
                    <DropdownMenu slot="list">
                        <DropdownItem @click.native="exportCsv">导出 CSV</DropdownItem>
                        <DropdownItem @click.native="exportExcel">导出 Excel</DropdownItem>
                    </DropdownMenu>
                </Dropdown>

                <Button v-if="enableColumnVisibility"
                    type="default"
                    size="small"
                    @click="columnDrawerVisible = true">
                    列设置
                </Button>
            </div>
        </div>

        <div id="main"
            class="main-table-wrapper"
            tabindex="0"
            @keydown="handleKeydown"
            :aria-busy="loading ? 'true' : 'false'">
            <OptimizedVxeTable v-if="useVxeVirtual"
                ref="vxeMainTable"
                :columns="processedColumns"
                :data="data"
                :loading="loading"
                :size="size"
                :ext-height="extHeight"
                :auto-height="autoHeight"
                :pagination="vxePagination"
                :page-size-options="pageSizeOptions"
                :show-page="showPage"
                @page-change="handleVxePageChange"
                @selection-change="handleSelectionChange"
                @row-dblclick="handleVxeRowDblClick" />

            <Table v-else
                ref="mainTable"
                :columns="processedColumns"
                :data="data"
                :loading="false"
                :size="size"
                :border="true"
                :stripe="true"
                :height="tableHeight"
                :row-class-name="rowClassName"
                @on-select="handleSelect"
                @on-selection-change="handleSelectionChange"
                @on-row-dblclick="handleRowDblClick"
                @on-column-width-resize="handleColumnWidthResize" />

            <div v-if="showSkeleton" class="table-skeleton" aria-hidden="true">
                <div class="skeleton-row skeleton-header">
                    <div v-for="n in ariaColCount"
                        :key="`h_${n}`"
                        class="skeleton-cell"></div>
                </div>
                <div v-for="r in skeletonRowCount"
                    :key="`r_${r}`"
                    class="skeleton-row">
                    <div v-for="n in ariaColCount"
                        :key="`c_${r}_${n}`"
                        class="skeleton-cell"></div>
                </div>
            </div>

            <div v-if="showEmpty" class="table-empty" role="status" aria-live="polite">
                <slot name="empty">
                    <div class="empty-illustration" aria-hidden="true"></div>
                    <div class="empty-text">
                        <div class="empty-title">暂无数据</div>
                        <div class="empty-subtitle">请调整筛选条件或刷新重试</div>
                    </div>
                    <Button type="primary" size="small" @click="handleRefreshClick">刷新</Button>
                </slot>
            </div>
        </div>

        <div v-show="showSummary" id="summary" class="summary-table-wrapper">
            <Table
                ref="summaryTable"
                class="summary-table"
                :columns="summaryColumns"
                :data="summaryData"
                :size="size"
                :show-header="false"
                :stripe="true"
                :border="true" />
        </div>

        <Page v-if="showPage && !useVxeVirtual"
            :total="total"
            :page-size="pageSize"
            :current="current"
            :page-size-opts="pageSizeOptions"
            class="pagination"
            size="small"
            show-elevator
            show-sizer
            show-total
            placement="top"
            aria-label="分页"
            @on-change="onPageChange"
            @on-page-size-change="onPageSizeChange" />

        <Drawer v-model="columnDrawerVisible"
            title="列设置"
            placement="right"
            width="320"
            :mask-closable="true">
            <div class="column-setting">
                <div class="column-setting-actions">
                    <Button size="small" type="default" :disabled="isAllVisible" @click="setAllVisible">全选</Button>
                    <Button size="small" type="default" :disabled="isNoneVisible" @click="setNoneVisible">反选</Button>
                    <Button size="small" type="default" @click="resetVisibility">重置</Button>
                    <Button size="small" type="default" @click="resetWidths">重置列宽</Button>
                </div>
                <div class="column-setting-list">
                    <Checkbox v-for="c in allColumnsForSetting"
                        :key="c.key"
                        :value="visibleKeySet.has(c.key)"
                        @on-change="() => toggleVisible(c.key)">
                        {{ c.title }}
                    </Checkbox>
                </div>
            </div>
        </Drawer>
    </div>
</template>

<script>
import search from '@/libs/search';
import { number2DecimalFormat, number2ThousandsFormat, calculate } from '@/libs/logic.js';
import XLSX from 'xlsx';
import { computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref, toRef, watch } from 'vue';
import { getUserId } from '@/libs/util';
import OptimizedVxeTable from '@/components/vxe-pagination-table/vxe-pagination-table.vue';
import { usePagination } from './composables/usePagination';
import { useTableColumns } from './composables/useTableColumns';
import { useTableData } from './composables/useTableData';

export default {
    name: 'StandPaginationTable',
    
    components: {
        OptimizedVxeTable
    },

    props: {
        columns: {
            type: Array,
            required: true
        },
        data: {
            type: Array,
            required: true
        },
        loading: {
            type: Boolean,
            required: true
        },
        size: {
            type: String,
            default: 'small',
            validator: value => ['small', 'default', 'large'].includes(value)
        },
        extHeight: {
            type: Number,
            default: 0
        },
        autoHeight: {
            type: Boolean,
            default: false
        },
        tableSearch: {
            type: Object,
            required: true
        },
        tableLoadEvent: {
            type: Function,
            required: true
        },
        pageSizeOptions: {
            type: Array,
            default: () => search.getSizeConfig()
        },
        showPage: {
            type: Boolean,
            default: true
        },
        summaryFields: {
            type: Array,
            default: () => []
        },
        summaryLabelField: {
            type: String,
            default: ''
        },
        totalSummaryFieldDatas: {
            type: Array,
            default: () => []
        },
        places: {
            type: Number,
            default: 2
        },
        tableKey: {
            type: String,
            default: ''
        },
        userId: {
            type: String,
            default: ''
        },
        enableVxeVirtual: {
            type: Boolean,
            default: false
        },
        virtualThreshold: {
            type: Number,
            default: 200
        },
        showToolbar: {
            type: Boolean,
            default: false
        },
        enableRefresh: {
            type: Boolean,
            default: true
        },
        refreshCooldownMs: {
            type: Number,
            default: 3000
        },
        enableExport: {
            type: Boolean,
            default: false
        },
        exportFileName: {
            type: String,
            default: 'table-export'
        },
        enableColumnVisibility: {
            type: Boolean,
            default: false
        },
        enableColumnWidthPersistence: {
            type: Boolean,
            default: false
        },
        defaultHiddenColumnKeys: {
            type: Array,
            default: () => []
        },
        exportColumnKeys: {
            type: Array,
            default: () => []
        },
        exportRowLimit: {
            type: Number,
            default: 50000
        },
        loadDebounceMs: {
            type: Number,
            default: 120
        },
        loadRetry: {
            type: Number,
            default: 1
        }
    },

    setup(props, { emit }) {
        const instance = getCurrentInstance();
        const proxy = instance && instance.proxy ? instance.proxy : null;

        const columnsRef = toRef(props, 'columns');
        const dataRef = toRef(props, 'data');
        const loadingRef = toRef(props, 'loading');
        const tableSearchRef = toRef(props, 'tableSearch');
        const summaryFieldsRef = toRef(props, 'summaryFields');
        const summaryLabelFieldRef = toRef(props, 'summaryLabelField');
        const totalSummaryFieldDatasRef = toRef(props, 'totalSummaryFieldDatas');
        const placesRef = toRef(props, 'places');
        const tableKeyRef = toRef(props, 'tableKey');

        const tableHeight = ref(400);
        const resizeHandler = ref(null);
        const scrollSyncHandler = ref(null);
        const columnDrawerVisible = ref(false);

        const resolvedUserId = computed(() => {
            if (props.userId) return String(props.userId);
            if (proxy && proxy.$store && proxy.$store.state && proxy.$store.state.user && proxy.$store.state.user.userId) {
                return String(proxy.$store.state.user.userId);
            }
            const cookieUserId = getUserId();
            return cookieUserId ? String(cookieUserId) : '';
        });

        const defaultHiddenKeysRef = computed(() => props.defaultHiddenColumnKeys || []);

        const columnManager = useTableColumns({
            columns: columnsRef,
            summaryLabelField: summaryLabelFieldRef,
            tableKey: tableKeyRef,
            userId: resolvedUserId,
            enableColumnVisibility: computed(() => props.enableColumnVisibility),
            enableColumnWidthPersistence: computed(() => props.enableColumnWidthPersistence),
            defaultHiddenKeys: defaultHiddenKeysRef
        });

        const {
            processedColumns,
            allColumnsForSetting,
            visibleKeySet,
            isAllVisible,
            isNoneVisible,
            setAllVisible,
            setNoneVisible,
            resetVisibility,
            toggleVisible,
            handleColumnWidthResize,
            resetWidths
        } = columnManager;

        const calculateColumnTotal = (key) => {
            return (dataRef.value || []).reduce((sum, item) => {
                const value = Number(item && item[key]);
                return calculate(sum, (isNaN(value) ? 0 : value), '+');
            }, 0);
        };

        const showSummary = computed(() => (summaryFieldsRef.value || []).length > 0 && (dataRef.value || []).length > 0);

        const summaryColumns = computed(() => {
            return (processedColumns.value || []).map(({ render, _render, ...rest }) => rest);
        });

        const summaryData = computed(() => {
            const hasTotalSummary = (totalSummaryFieldDatasRef.value || []).length > 0;
            const totalKeys = hasTotalSummary
                ? [...new Set((totalSummaryFieldDatasRef.value || []).flatMap(item => Object.keys(item || {})))]
                : [];

            const createSummaryRow = (sourceType = 'current') => {
                const summary = {};

                (processedColumns.value || []).forEach(column => {
                    const key = column && column.key;
                    if (!key) return;

                    if (summaryLabelFieldRef.value && key === summaryLabelFieldRef.value) {
                        summary[key] = sourceType === 'current' ? '总计' : '总和';
                        return;
                    }

                    const shouldInclude = sourceType === 'current'
                        ? (summaryFieldsRef.value || []).includes(key)
                        : hasTotalSummary && totalKeys.includes(key);

                    if (!shouldInclude) {
                        summary[key] = '';
                        return;
                    }

                    const value = sourceType === 'current'
                        ? calculateColumnTotal(key)
                        : ((totalSummaryFieldDatasRef.value || [])[0] || {})[key] || 0;

                    const decimalPlaces = typeof column.decimalPlaces === 'number'
                        ? column.decimalPlaces
                        : (placesRef.value || 2);
                    const symbol = column.isCurrencyField ? '￥' : '';

                    summary[key] = column.quantity
                        ? number2DecimalFormat(value, decimalPlaces)
                        : number2ThousandsFormat(value, decimalPlaces, symbol);
                });

                return summary;
            };

            const summaries = [createSummaryRow('current')];
            if (hasTotalSummary) summaries.push(createSummaryRow('total'));
            return summaries;
        });

        const initializeTableHeight = () => {
            if (props.autoHeight) {
                tableHeight.value = null;
                return;
            }
            const tableRef = proxy && proxy.$refs ? proxy.$refs.mainTable : null;
            if (!tableRef || !tableRef.$el) return;
            const offsetTop = tableRef.$el.offsetTop;
            tableHeight.value = window.innerHeight - offsetTop - 240 - (props.extHeight || 0);
        };

        const setupResizeListener = () => {
            resizeHandler.value = (() => {
                let timer = null;
                return () => {
                    if (timer) clearTimeout(timer);
                    timer = setTimeout(() => initializeTableHeight(), 200);
                };
            })();
            window.addEventListener('resize', resizeHandler.value);
        };

        const cleanupResizeListener = () => {
            if (resizeHandler.value) {
                window.removeEventListener('resize', resizeHandler.value);
                resizeHandler.value = null;
            }
        };

        const cleanupScrollSync = () => {
            if (!scrollSyncHandler.value) return;
            const summary = proxy && proxy.$refs && proxy.$refs.summaryTable && proxy.$refs.summaryTable.$el
                ? proxy.$refs.summaryTable.$el.querySelector('.ivu-table-body')
                : null;
            if (summary) summary.removeEventListener('scroll', scrollSyncHandler.value);
            scrollSyncHandler.value = null;
        };

        const setupScrollSync = () => {
            nextTick(() => {
                const main = proxy && proxy.$refs && proxy.$refs.mainTable && proxy.$refs.mainTable.$el
                    ? proxy.$refs.mainTable.$el.querySelector('.ivu-table-body')
                    : null;
                const summary = proxy && proxy.$refs && proxy.$refs.summaryTable && proxy.$refs.summaryTable.$el
                    ? proxy.$refs.summaryTable.$el.querySelector('.ivu-table-body')
                    : null;

                if (!main || !summary) return;

                scrollSyncHandler.value = () => {
                    main.scrollLeft = summary.scrollLeft;
                };
                summary.addEventListener('scroll', scrollSyncHandler.value);
            });
        };

        watch(dataRef, () => {
            nextTick(() => {
                initializeTableHeight();
            });
        }, { deep: true });

        const tableLoader = useTableData({
            load: props.tableLoadEvent,
            debounceMs: computed(() => props.loadDebounceMs),
            retry: computed(() => props.loadRetry),
            refreshCooldownMs: computed(() => props.refreshCooldownMs)
        });

        const { refresh, refreshRemainingMs } = tableLoader;

        const pagination = usePagination({
            tableSearch: tableSearchRef,
            tableLoad: () => tableLoader.debouncedLoad()
        });

        const { total, pageSize, current, onPageChange, onPageSizeChange } = pagination;

        const hasUnsafeColumn = computed(() => {
            return (processedColumns.value || []).some((c) => !!(c && (c.render || c.renderHeader || c.slot || c.slots)));
        });

        const useVxeVirtual = computed(() => {
            if (!props.enableVxeVirtual) return false;
            if ((dataRef.value || []).length < (props.virtualThreshold || 200)) return false;
            return !hasUnsafeColumn.value;
        });

        const virtualManualEnabled = ref(null);
        const virtualSwitchAvailable = computed(() => {
            if (!props.enableVxeVirtual) return false;
            if ((dataRef.value || []).length < (props.virtualThreshold || 200)) return false;
            return !hasUnsafeColumn.value;
        });

        const virtualSwitchValue = computed(() => {
            if (!virtualSwitchAvailable.value) return false;
            if (virtualManualEnabled.value === null) return true;
            return !!virtualManualEnabled.value;
        });

        const handleVirtualToggle = (next) => {
            if (!virtualSwitchAvailable.value) {
                virtualManualEnabled.value = false;
                return;
            }
            virtualManualEnabled.value = !!next;
        };

        const useVxeVirtualEffective = computed(() => {
            if (!virtualSwitchAvailable.value) return false;
            if (virtualManualEnabled.value === null) return true;
            return !!virtualManualEnabled.value;
        });

        const vxePagination = computed(() => {
            return {
                currentPage: current.value || 1,
                pageSize: pageSize.value || 10,
                total: total.value || 0
            };
        });

        const handleVxePageChange = ({ currentPage, pageSize: nextPageSize }) => {
            if (typeof currentPage === 'number') search.globalPageOnChange(props.tableSearch, currentPage);
            if (typeof nextPageSize === 'number') search.globalSizeOnChange(props.tableSearch, nextPageSize);
            tableLoader.debouncedLoad();
        };

        const handleSelect = (selection, row) => {
            emit('on-select', selection, row);
        };

        const handleSelectionChange = (selection) => {
            emit('on-selection-change', selection);
        };

        const handleRowDblClick = (row, index) => {
            emit('on-row-dblclick', row, index);
        };

        const handleVxeRowDblClick = (row, field) => {
            emit('on-row-dblclick', row, field);
        };

        const activeRowIndex = ref(-1);
        watch(dataRef, () => {
            const len = (dataRef.value || []).length;
            if (len === 0) {
                activeRowIndex.value = -1;
                return;
            }
            if (activeRowIndex.value < 0) activeRowIndex.value = 0;
            if (activeRowIndex.value > len - 1) activeRowIndex.value = len - 1;
        }, { deep: true, immediate: true });

        const scrollToActiveRow = () => {
            nextTick(() => {
                const tableRef = proxy && proxy.$refs ? proxy.$refs.mainTable : null;
                if (!tableRef || !tableRef.$el) return;
                const body = tableRef.$el.querySelector('.ivu-table-body');
                if (!body) return;
                const rows = body.querySelectorAll('tr.ivu-table-row');
                const idx = activeRowIndex.value;
                if (idx < 0 || idx >= rows.length) return;
                const rowEl = rows[idx];
                const bodyRect = body.getBoundingClientRect();
                const rowRect = rowEl.getBoundingClientRect();
                const overTop = rowRect.top < bodyRect.top;
                const overBottom = rowRect.bottom > bodyRect.bottom;
                if (!overTop && !overBottom) return;
                const delta = overTop ? (rowRect.top - bodyRect.top) : (rowRect.bottom - bodyRect.bottom);
                body.scrollTop += delta;
            });
        };

        const handleKeydown = (e) => {
            if (!e) return;
            if (useVxeVirtualEffective.value) return;
            if (loadingRef.value) return;
            const len = (dataRef.value || []).length;
            if (len === 0) return;

            const key = e.key;
            const step = 10;
            let nextIndex = activeRowIndex.value;
            if (key === 'ArrowDown') nextIndex += 1;
            else if (key === 'ArrowUp') nextIndex -= 1;
            else if (key === 'Home') nextIndex = 0;
            else if (key === 'End') nextIndex = len - 1;
            else if (key === 'PageDown') nextIndex += step;
            else if (key === 'PageUp') nextIndex -= step;
            else return;

            e.preventDefault();
            nextIndex = Math.max(0, Math.min(len - 1, nextIndex));
            if (nextIndex !== activeRowIndex.value) {
                activeRowIndex.value = nextIndex;
                scrollToActiveRow();
            }
        };

        const rowClassName = (row, index) => {
            const base = row && row.rowClass ? row.rowClass : '';
            const active = typeof index === 'number' && index === activeRowIndex.value ? 'row-active' : '';
            return [base, active].filter(Boolean).join(' ');
        };

        const ariaRowCount = computed(() => (dataRef.value || []).length);
        const ariaColCount = computed(() => Math.max(1, (processedColumns.value || []).length));
        const skeletonRowCount = computed(() => Math.min(10, Math.max(6, Math.floor((tableHeight.value || 400) / 40))));
        const showSkeleton = computed(() => !!loadingRef.value);
        const showEmpty = computed(() => !loadingRef.value && (dataRef.value || []).length === 0);

        const handleRefreshClick = () => {
            refresh();
        };

        const pickExportColumns = () => {
            const cols = processedColumns.value || [];
            const keys = (props.exportColumnKeys || []).map((k) => String(k));
            const candidate = cols.filter((c) => c && c.key && !c.type);
            if (keys.length === 0) return candidate;
            return candidate.filter((c) => keys.includes(String(c.key)));
        };

        const toCsvCell = (value) => {
            const s = value === null || value === undefined ? '' : String(value);
            const escaped = s.replace(/"/g, '""');
            return /[",\r\n]/.test(escaped) ? `"${escaped}"` : escaped;
        };

        const exportCsv = async () => {
            const rows = (dataRef.value || []).slice(0, Math.max(0, Number(props.exportRowLimit || 0)));
            const cols = pickExportColumns();
            const header = cols.map((c) => toCsvCell(c.title || c.key || '')).join(',');

            const useWorker = rows.length >= 2000;
            let csv = '';

            if (useWorker && typeof Worker !== 'undefined') {
                const workerCode = `
                    const toCell = (v) => {
                        const s = v === null || v === undefined ? '' : String(v);
                        const e = s.replace(/"/g, '""');
                        return /[",\\r\\n]/.test(e) ? '"' + e + '"' : e;
                    };
                    self.onmessage = (e) => {
                        const { rows, cols } = e.data || {};
                        const header = (cols || []).map(c => toCell(c.title || c.key || '')).join(',');
                        const lines = (rows || []).map(r => (cols || []).map(c => toCell(r && c.key ? r[c.key] : '')).join(','));
                        self.postMessage({ csv: header + "\\r\\n" + lines.join("\\r\\n") });
                    };
                `;
                const blobUrl = URL.createObjectURL(new Blob([workerCode], { type: 'application/javascript' }));
                try {
                    csv = await new Promise((resolve, reject) => {
                        const worker = new Worker(blobUrl);
                        worker.onmessage = (msg) => {
                            worker.terminate();
                            resolve((msg.data || {}).csv || '');
                        };
                        worker.onerror = (err) => {
                            worker.terminate();
                            reject(err);
                        };
                        worker.postMessage({
                            rows,
                            cols: cols.map((c) => ({ key: c.key, title: c.title }))
                        });
                    });
                }
                catch (e) {
                }
                finally {
                    URL.revokeObjectURL(blobUrl);
                }
            }
            else {
                const dataLines = rows.map((row) => {
                    return cols.map((c) => toCsvCell(row && c.key ? row[c.key] : '')).join(',');
                });
                csv = `${header}\r\n${dataLines.join('\r\n')}`;
            }
            if (!csv) {
                const dataLines = rows.map((row) => {
                    return cols.map((c) => toCsvCell(row && c.key ? row[c.key] : '')).join(',');
                });
                csv = `${header}\r\n${dataLines.join('\r\n')}`;
            }

            const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${props.exportFileName || 'table-export'}.csv`;
            link.click();
            URL.revokeObjectURL(url);
        };

        const exportExcel = () => {
            const rows = (dataRef.value || []).slice(0, Math.max(0, Number(props.exportRowLimit || 0)));
            const cols = pickExportColumns();
            const headerKeys = cols.map((c) => c.key);
            const headerTitles = cols.map((c) => c.title || c.key);
            const aoa = [headerTitles];
            rows.forEach((row) => {
                aoa.push(headerKeys.map((k) => (row && k ? row[k] : '')));
            });
            const ws = XLSX.utils.aoa_to_sheet(aoa);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
            XLSX.writeFile(wb, `${props.exportFileName || 'table-export'}.xlsx`);
        };

        onMounted(() => {
            initializeTableHeight();
            setupResizeListener();
            setupScrollSync();
        });

        onBeforeUnmount(() => {
            cleanupResizeListener();
            cleanupScrollSync();
        });

        return {
            tableHeight,
            columnDrawerVisible,
            processedColumns,
            summaryColumns,
            showSummary,
            summaryData,
            handleSelect,
            handleSelectionChange,
            handleRowDblClick,
            handleVxeRowDblClick,
            onPageChange,
            onPageSizeChange,
            total,
            pageSize,
            current,
            rowClassName,
            ariaRowCount,
            ariaColCount,
            showSkeleton,
            showEmpty,
            skeletonRowCount,
            handleRefreshClick,
            refreshRemainingMs,
            exportCsv,
            exportExcel,
            enableExport: computed(() => props.enableExport),
            enableRefresh: computed(() => props.enableRefresh),
            showToolbar: computed(() => props.showToolbar),
            enableColumnVisibility: computed(() => props.enableColumnVisibility),
            allColumnsForSetting,
            visibleKeySet,
            isAllVisible,
            isNoneVisible,
            setAllVisible,
            setNoneVisible,
            resetVisibility,
            toggleVisible,
            handleColumnWidthResize,
            resetWidths,
            enableVxeVirtual: computed(() => props.enableVxeVirtual),
            virtualSwitchAvailable,
            virtualSwitchValue,
            handleVirtualToggle,
            useVxeVirtual: useVxeVirtualEffective,
            vxePagination,
            handleVxePageChange,
            handleKeydown
        };
    }
};
</script>

<style lang="less" scoped>
.stand-pagination-table {
    display: flex;
    flex-direction: column;
    position: relative;

    .main-table-wrapper /deep/ .ivu-table-body {
        overflow-x: hidden;
    }

    /deep/ .ivu-table-body {
        &::-webkit-scrollbar {
            height: 20px;
        }

        &::-webkit-scrollbar-thumb {
            background-color: #C1C1C1;
        }
    }

    .summary-table {
        margin-top: 0;
        border-top-color: #FFFFFF;
        width: 100%; 
    }

    .pagination {
        margin-top: 8px;
        width: 100%;
    }

    .table-toolbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 8px 0;
        gap: 8px;

        .toolbar-left,
        .toolbar-right {
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .virtual-toggle {
            display: flex;
            align-items: center;
            gap: 6px;
        }

        .virtual-toggle-label {
            font-size: 12px;
            color: #606266;
            user-select: none;
        }

        /deep/ .ivu-btn:focus {
            outline-offset: 2px;
        }
    }

    .main-table-wrapper {
        position: relative;
        min-height: 120px;
    }
    
    .main-table-wrapper:focus {
        outline-offset: 2px;
        border-radius: 6px;
    }

    /deep/ .ivu-table-row.row-active td {
        background: #ecf5ff;
    }

    .table-skeleton {
        position: absolute;
        inset: 0;
        background: rgba(255, 255, 255, 0.96);
        z-index: 2;
        padding: 12px;
        overflow: hidden;
        pointer-events: none;
    }

    .skeleton-row {
        display: flex;
        gap: 8px;
        margin-bottom: 10px;
    }

    .skeleton-header {
        margin-bottom: 12px;
    }

    .skeleton-cell {
        flex: 1;
        height: 14px;
        border-radius: 6px;
        background: linear-gradient(90deg, #f1f3f5 25%, #e9ecef 37%, #f1f3f5 63%);
        background-size: 400% 100%;
        animation: skeleton-loading 1.2s ease infinite;
    }

    @keyframes skeleton-loading {
        0% {
            background-position: 100% 0;
        }
        100% {
            background-position: 0 0;
        }
    }

    .table-empty {
        position: absolute;
        inset: 0;
        z-index: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 10px;
        padding: 16px;
        background: rgba(255, 255, 255, 0.98);
    }

    .empty-illustration {
        width: 160px;
        height: 100px;
        border-radius: 14px;
        background:
            radial-gradient(circle at 30% 35%, #e9ecef 0 18px, transparent 19px),
            radial-gradient(circle at 70% 40%, #e9ecef 0 14px, transparent 15px),
            linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%);
        border: 1px solid #f1f3f5;
    }

    .empty-text {
        text-align: center;
        .empty-title {
            font-size: 14px;
            color: #303133;
            font-weight: 600;
        }
        .empty-subtitle {
            margin-top: 4px;
            font-size: 12px;
            color: #909399;
        }
    }

    .column-setting {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .column-setting-actions {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
    }

    .column-setting-list {
        max-height: calc(100vh - 220px);
        overflow: auto;
        padding-right: 4px;
        /deep/ .ivu-checkbox-wrapper {
            display: flex;
            align-items: center;
            margin: 8px 0;
        }
        /deep/ .ivu-checkbox-wrapper:focus-within {
            outline: 2px solid #2d8cf0;
            outline-offset: 2px;
            border-radius: 6px;
        }
    }
    
}
</style>
